import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type IconStateIndicatorPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const positionClasses: Record<IconStateIndicatorPosition, string> = {
    'top-left': 'top-[8.33%] -left-[12.5%]',
    'top-right': 'top-[8.33%] -right-[12.5%]',
    'bottom-left': '-bottom-[12.5%] -left-[12.5%]',
    'bottom-right': '-bottom-[12.5%] -right-[12.5%]',
};

export type IconStateProps = Omit<ComponentPropsWithRef<'div'>, 'children' | 'content'> & {
    children: ReactNode;
    /** Corner position of the indicator relative to the icon. */
    position?: IconStateIndicatorPosition;
    /** Tailwind colour class for the indicator background (e.g. `bg-snk-cart-green`). */
    color?: string;
    /** When set, renders a badge instead of a plain dot. Accepts text (e.g. "5") or a ReactNode (e.g. an SVG icon). */
    content?: ReactNode;
    /** When false the indicator is not rendered. */
    visible?: boolean;
    /** Accessible name for the indicator. When omitted the indicator is `aria-hidden`. */
    indicatorLabel?: string;
    /** Extra classes merged onto the indicator element. */
    indicatorClassName?: string;
};

export const IconState = ({
    children,
    className,
    position = 'top-right',
    color,
    content,
    visible = true,
    indicatorLabel,
    indicatorClassName,
    ref,
    ...props
}: IconStateProps) => {
    const hasContent = content != null && content !== '';
    const isTextContent = typeof content === 'string' || typeof content === 'number';

    return (
        <div ref={ref} data-slot="icon-state" className={cn('relative inline-flex', className)} {...props}>
            {children}

            {visible && (
                <div className="@container pointer-events-none absolute inset-0">
                    <span
                        data-slot="icon-state-indicator"
                        data-position={position}
                        className={cn(
                            'pointer-events-auto absolute z-[1] flex shrink-0 items-center justify-center rounded-full outline outline-1 outline-primary-foreground',
                            'h-1/2 w-1/2 text-primary-foreground',
                            positionClasses[position],
                            isTextContent && 'text-[length:35cqi] font-medium leading-none',
                            color,
                            indicatorClassName
                        )}
                        aria-hidden={indicatorLabel ? undefined : true}
                        aria-label={indicatorLabel}>
                        {hasContent ? (
                            <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{content}</span>
                        ) : null}
                        {/* TODO: once SNKSN-84 is merged - we can use text-trim-cap utility class instead of inline style */}
                    </span>
                </div>
            )}
        </div>
    );
};
